import { THREE, ThreeView } from 'uweb/three'
import { MapView } from 'uweb/maptalks'
import { log, KeyValue } from 'utils/web'
import { Point, UTM } from 'uweb/utils'

type SurfaceStyle = {
    top: number          // color at higher elevation
    bottom: number       // color at lower elevation
    roughness: number
    opacity: number
}

const SURFACE_STYLES: Record<string, SurfaceStyle> = {
    grass: { top: 0x7cb342, bottom: 0x4b7e2c, roughness: 0.9, opacity: 0.5 },   // lawn green
    topsoil: { top: 0x4a3220, bottom: 0x6f4e37, roughness: 0.97, opacity: 1.0 },   // bare earth
}

export class Triangle {

    isM = false
    Three
    Maptalks
    Point

    obj: any = {}

    constructor({ Maptalks, Three }: { Maptalks?: MapView, Three?: ThreeView }) {

        this.Maptalks = Maptalks
        this.Three = Three
        if (Maptalks && Three) this.Point = new Point({ Maptalks, Three })

    }

    altitude = (el: number) => {
        const elevation = el || 1500
        const vector3 = this.Maptalks?.threeLayer.distanceToVector3(elevation, elevation)
        const zPos = vector3.x
        return zPos
    }

    add = (group: any, rows: tItem[], styleName: 'grass' | 'topsoil' = 'grass') => {

        try {
            const style = SURFACE_STYLES[styleName]
            const is_3D_enabled = KeyValue('3D') === 'yes'
            const is_3D_diff = Number(KeyValue('Elevation'))
            const [nm, lt] = KeyValue('Zone').split(',')
            const is = (m: any[]) => !isNaN(m[0]) && !isNaN(m[1]) && !isNaN(m[2])

            const baseGeometry = new THREE.BufferGeometry()
            baseGeometry.setAttribute('position', new THREE.Float32BufferAttribute([
                0, 0, 0,
                1, 0, 0,
                0, 1, 0,
            ], 3))
            baseGeometry.setAttribute('normal', new THREE.Float32BufferAttribute([
                0, 0, 1,
                0, 0, 1,
                0, 0, 1,
            ], 3))

            const material = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                flatShading: true,
                roughness: style.roughness,
                metalness: 0,
                side: THREE.DoubleSide,
                transparent: style.opacity < 1,
                opacity: style.opacity,
                depthWrite: style.opacity >= 1,   // translucent design surface won't occlude
            })

            const mesh = new THREE.InstancedMesh(baseGeometry, material, rows.length)
            mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)

            const matrix = new THREE.Matrix4()
            let count = 0
            let anchor: THREE.Vector3 | null = null
            const zs: number[] = []
            let zMin = Infinity, zMax = -Infinity

            for (const n of rows) {
                const j: [number, number, number][] = []
                for (const x of n.Coords[0]) {
                    if (!is(x)) continue
                    const ll = UTM.convertUtmToLatLng(x[0], x[1], nm, lt)
                    const arg: any = { x: Number(ll.lng), y: Number(ll.lat), z: 0 }
                    const f: any = this.Maptalks?.threeLayer.coordinateToVector3(arg, 0)
                    j.push([f.x, f.y, is_3D_enabled ? this.altitude(x[2] - is_3D_diff) : 0])
                }
                if (j.length < 3) continue

                const A = new THREE.Vector3(...j[0])
                const B = new THREE.Vector3(...j[1])
                const C = new THREE.Vector3(...j[2])
                if (!anchor) anchor = A.clone()

                const Arel = new THREE.Vector3().subVectors(A, anchor)
                const col0 = new THREE.Vector3().subVectors(B, A)
                const col1 = new THREE.Vector3().subVectors(C, A)
                const col2 = new THREE.Vector3().crossVectors(col0, col1).normalize()

                matrix.set(
                    col0.x, col1.x, col2.x, Arel.x,
                    col0.y, col1.y, col2.y, Arel.y,
                    col0.z, col1.z, col2.z, Arel.z,
                    0, 0, 0, 1,
                )

                const cz = (A.z + B.z + C.z) / 3
                zs.push(cz)
                if (cz < zMin) zMin = cz
                if (cz > zMax) zMax = cz
                mesh.setMatrixAt(count++, matrix)
            }

            const high = new THREE.Color(style.top)
            const low = new THREE.Color(style.bottom)
            const span = (zMax - zMin) || 1
            const tmp = new THREE.Color()
            for (let i = 0; i < count; i++) {
                const t = (zs[i] - zMin) / span
                tmp.lerpColors(low, high, t)
                const jit = 0.85 + Math.random() * 0.3
                tmp.setRGB(tmp.r * jit, tmp.g * jit, tmp.b * jit)
                mesh.setColorAt(i, tmp)
            }
            mesh.instanceColor!.needsUpdate = true

            if (anchor) mesh.position.copy(anchor)
            mesh.count = count
            mesh.instanceMatrix.needsUpdate = true
            group.add(mesh)

        } catch (err: any) { log.error(`[Triangle.add()]: ${err.message}`) }
    }

    remove = (key: string) => {

        if (this.obj.hasOwnProperty(key)) {

            const group = this.obj[key]
            this.Maptalks?.threeLayer.removeMesh(group)
            const nodes: any = []
            group.traverse((child: any) => { nodes.push(child) })
            nodes.forEach((node: any) => { node.removeFromParent() })
            group.clear()
            delete this.obj[key]

        }

    }

    remove_all = () => {
        for (const x in this.obj) this.remove(x)
    }

    insert = (key: string, rows: tItem[]) => {

        if (this.obj.hasOwnProperty(key)) this.remove(key)
        else {

            const group = new THREE.Group()
            this.obj[key] = group
            this.add(group, rows)

            group.userData.type = 'target-surface'
            group.userData.surfaceId = key

            this.Maptalks?.threeLayer.addMesh(group)

        }

    }

}